import { useEffect, useRef } from 'react';

const MatrixBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Matrix characters - legal/tech themed with law icons
    // Using Unicode symbols that render properly in monospace fonts
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*()§¶←→↑↓⚖†';
    const lawIcons = ['⚖', '†']; // Law scales (U+2696), dagger/legal marker (U+2020)
    const fontSize = 14;
    const columns = Math.floor(canvas.width / fontSize);

    // Array to track y position of drops
    const drops = Array(columns).fill(1);

    // Enhanced gold colors with more visibility
    const goldColor = 'rgba(255, 215, 0, 0.25)'; // More visible
    const goldColorBright = 'rgba(255, 215, 0, 0.5)'; // Brighter for leading char
    const goldColorGlow = 'rgba(218, 165, 32, 0.4)'; // Additional glow color

    function draw() {
      // Black background with slight transparency for trail effect
      ctx.fillStyle = 'rgba(26, 26, 26, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < drops.length; i++) {
        // Occasionally use law icons (7.5% chance - half of before), otherwise regular characters
        let char;
        const useLawIcon = Math.random() < 0.075;
        
        if (useLawIcon) {
          char = lawIcons[Math.floor(Math.random() * lawIcons.length)];
        } else {
          char = chars[Math.floor(Math.random() * chars.length)];
        }
        
        const x = i * fontSize;
        const y = drops[i] * fontSize;
        
        // Special styling for law icons
        if (lawIcons.includes(char)) {
          // Law icons - smaller size and vibrant gold color
          ctx.font = `${fontSize * 0.7}px monospace`; // 30% smaller
          ctx.shadowBlur = 25;
          ctx.shadowColor = 'rgba(255, 215, 0, 0.8)'; // Bright gold glow
          ctx.fillStyle = 'rgba(255, 215, 0, 0.9)'; // Vibrant gold
        } else if (i % 3 === 0) {
          // Leading character with strong glow
          ctx.font = `${fontSize}px monospace`; // Normal size
          ctx.shadowBlur = 15;
          ctx.shadowColor = goldColorGlow;
          ctx.fillStyle = goldColorBright;
        } else {
          // Regular characters with subtle glow
          ctx.font = `${fontSize}px monospace`; // Normal size
          ctx.shadowBlur = 8;
          ctx.shadowColor = 'rgba(218, 165, 32, 0.2)';
          ctx.fillStyle = goldColor;
        }
        
        // Draw character
        ctx.fillText(char, x, y);
        
        // Reset shadow and font after drawing
        ctx.shadowBlur = 0;
        ctx.font = `${fontSize}px monospace`;

        // Reset drop to top randomly after it crosses the screen
        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }

        // Increment y coordinate
        drops[i]++;
      }
    }

    // Animation interval - slower for subtle effect
    const interval = setInterval(draw, 50);

    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      drops.length = Math.floor(canvas.width / fontSize);
      drops.fill(1);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1, // Behind ALL content
        pointerEvents: 'none',
        opacity: 0.6 // Additional opacity layer for extra subtlety
      }}
    />
  );
};

export default MatrixBackground;
